import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/auth-context";
import {
  fetchAuthConfig,
  googleLoginHref,
  PasswordLoginError,
  type AuthConfig,
  type PasswordLoginReason,
} from "../../auth/auth-client";
import { FullScreenLoader } from "../../auth/full-screen-loader";
import { FROM_PARAM, safeReturnPath } from "../../auth/session-expired";
import { AppRoutes } from "../app/routes";
import "./login.css";

interface FromState {
  from?: { pathname?: string };
}

/**
 * Login screen. Interim sign-in is email + password (DMS auth plan) — verified
 * server-side by the BFF against the backend, which mints the httpOnly session
 * (no token in the browser). When the Cognito standard is wired the BFF reports
 * `googleEnabled` and the screen shows the "Sign in with Google" CTA instead.
 * The corporate domains (@vana.gt, @hazloeasy.com) are enforced server-side
 * either way.
 */
export const Login = () => {
  const { t } = useTranslation("shell");
  const { status, signInWithPassword } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorReason, setErrorReason] = useState<PasswordLoginReason | null>(
    searchParams.has("error") ? "server" : null,
  );

  useEffect(() => {
    let active = true;
    fetchAuthConfig()
      .then((config) => {
        if (active) setAuthConfig(config);
      })
      .catch(() => {
        if (active) setAuthConfig({ googleEnabled: false, devLoginEnabled: false, passwordEnabled: true });
      });
    return () => {
      active = false;
    };
  }, []);

  if (status === "loading") return <FullScreenLoader />;

  // Where to go after signing in: the router state `RequireAuth` sets on an
  // in-app redirect, or the `?from=` a full-page redirect carries when the BFF
  // session expired mid-task (auth/session-expired.ts). Internal paths only.
  const from =
    (location.state as FromState)?.from?.pathname ??
    safeReturnPath(searchParams.get(FROM_PARAM)) ??
    AppRoutes.ROOT;

  if (status === "authenticated") {
    return <Navigate to={from} replace />;
  }

  const startGoogle = () => {
    setErrorReason(null);
    setSubmitting(true);
    window.location.assign(googleLoginHref(from));
  };

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault();
    setErrorReason(null);
    setSubmitting(true);
    try {
      await signInWithPassword(email.trim(), password);
      // Auth state flips to "authenticated"; the Navigate above redirects.
    } catch (error) {
      const reason = error instanceof PasswordLoginError ? error.reason : "server";
      setErrorReason(reason);
      setSubmitting(false);
    }
  };

  const errorText = errorReason ? t(`login_error_${errorReason}`) : "";

  return (
    <div className="login">
      <div className="login-frame">
        <div className="login-brand">
          <img className="login-brand-logo" src="/vanacard-logo.svg" alt="VanaCard" />
        </div>

        <h1 className="login-headline">{t("login_headline")}</h1>
        <p className="login-sub">{t("login_sub")}</p>

        {authConfig?.googleEnabled ? (
          <div className="login-actions">
            {errorReason && <p className="login-error">{errorText}</p>}
            <button
              type="button"
              className="login-button"
              onClick={startGoogle}
              disabled={submitting}
            >
              {t("login_cta")}
            </button>
            <p className="login-note">{t("login_domain_note")}</p>
          </div>
        ) : (
          <form className="login-actions" onSubmit={submitPassword}>
            {errorReason && <p className="login-error">{errorText}</p>}
            <label className="login-field">
              <span className="login-field-label">{t("login_email_label")}</span>
              <input
                type="email"
                className="login-input"
                value={email}
                autoComplete="username"
                placeholder="nombre@vana.gt"
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label className="login-field">
              <span className="login-field-label">{t("login_password_label")}</span>
              <input
                type="password"
                className="login-input"
                value={password}
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
            <button
              type="submit"
              className="login-button"
              disabled={submitting || authConfig === null || email === "" || password === ""}
            >
              {t("login_submit")}
            </button>
            <p className="login-note">{t("login_domain_note")}</p>
          </form>
        )}
      </div>
    </div>
  );
};
